import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, BookImage, Expand, ImagePlus, ListOrdered, LoaderCircle, LockKeyhole, MessageCircle, PencilLine, Send, Star, Trash2, X } from "lucide-react";
import { cropStyle } from "../lib/crop";

import PhotoShare from "./PhotoShare";
import { photoSharePath, sharedPhotoIndex } from "../lib/photoShare";

const commentDate = (value) => new Intl.DateTimeFormat("en", {
  month: "short",
  day: "numeric",
  year: "numeric",
}).format(new Date(value));

export default function PhotoStage({ album, onUnlock, emptyAction, curation, comments, sharePath, requestedPhotoId = "" }) {
  const [index, setIndex] = useState(0);
  const [full, setFull] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentDraft, setCommentDraft] = useState("");
  const [editingComment, setEditingComment] = useState(null);
  const [commentSaving, setCommentSaving] = useState(false);
  const [commentError, setCommentError] = useState("");
  const [orderOpen, setOrderOpen] = useState(false);
  const [targetPosition, setTargetPosition] = useState(1);
  const [orderError, setOrderError] = useState("");
  const photos = album?.photos || [];
  useEffect(() => setIndex(0), [album?.id]);
  const stage = useRef(null);
  const appliedLink = useRef("");
  useEffect(() => {
    const key = `${album?.id}:${requestedPhotoId}`;
    const target = sharedPhotoIndex(photos, requestedPhotoId);
    if (target < 0) { appliedLink.current = ""; return; }
    if (appliedLink.current === key) return;
    setIndex(target);
    setFull(false);
    const frame = requestAnimationFrame(() => {
      appliedLink.current = key;
      stage.current?.scrollIntoView({ block: "start", behavior: "instant" });
      stage.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [album?.id, photos, requestedPhotoId]);


  useEffect(() => setIndex((value) => value >= photos.length ? 0 : value), [photos.length]);
  useEffect(() => {
    setCommentsOpen(false);
    setCommentDraft("");
    setEditingComment(null);
    setCommentError("");
    setOrderOpen(false);
    setOrderError("");
  }, [album?.id, index]);
  useEffect(() => {
    function keyboard(event) {
      if (!photos.length || event.target.closest("input, textarea, select, [contenteditable=true]")) return;
      if (event.key === "ArrowLeft") setIndex((value) => (value - 1 + photos.length) % photos.length);
      if (event.key === "ArrowRight") setIndex((value) => (value + 1) % photos.length);
      if (event.key === "Escape") setFull(false);
    }
    window.addEventListener("keydown", keyboard);
    return () => window.removeEventListener("keydown", keyboard);
  }, [photos.length]);

  if (album?.locked) {
    return (
      <div className="photo-stage photo-stage--empty">
        <LockKeyhole aria-hidden="true" />
        <h3>Family album</h3>
        <p>This album is visible after member sign in.</p>
        <button className="text-link" type="button" onClick={onUnlock}>Unlock album</button>
      </div>
    );
  }

  if (!photos.length) {
    return (
      <div className="photo-stage photo-stage--empty">
        <div className="photo-stage__empty-visual" aria-hidden="true">
          <span /><span /><span />
          <ImagePlus />
          <b>ADD<br />THE<br />MOMENT</b>
        </div>
        <div className="photo-stage__empty-copy">
          <p className="kicker">Archive in progress</p>
          <h3>Bring this chapter into focus.</h3>
          <p>The story is here. Add the photographs that belong with it and give the class a shared album instead of another disappearing chat thread.</p>
          {emptyAction}
        </div>
      </div>
    );
  }

  const visibleIndex = index >= photos.length ? 0 : index;
  const photo = photos[visibleIndex];
  const move = (direction) => setIndex((value) => (value + direction + photos.length) % photos.length);
  const moving = curation?.movingPhotoId === photo.id;
  const editing = curation?.editingPhotoId === photo.id;
  const deleting = curation?.deletingPhotoId === photo.id;
  const makingCover = curation?.coveringPhotoId === photo.id;
  const reordering = curation?.reorderingPhotoId === photo.id;
  const isCover = curation?.coverPhotoSrc === photo.src;

  async function savePhotoOrder(event) {
    event.preventDefault();
    const position = Number(targetPosition);
    if (!Number.isInteger(position) || position < 1 || position > photos.length) {
      setOrderError(`Choose a position between 1 and ${photos.length}.`);
      return;
    }
    if (position === visibleIndex + 1) return;
    setOrderError("");
    try {
      await curation.reorder(photo, position);
      setIndex(position - 1);
      setOrderOpen(false);
    } catch (error) {
      setOrderError(error.message);
    }
  }

  async function saveComment(event) {
    event.preventDefault();
    if (!commentDraft.trim() || commentSaving) return;
    setCommentSaving(true);
    setCommentError("");
    try {
      if (editingComment) await comments.edit(photo, editingComment, commentDraft);
      else await comments.add(photo, commentDraft);
      setCommentDraft("");
      setEditingComment(null);
    } catch (error) {
      setCommentError(error.message);
    } finally {
      setCommentSaving(false);
    }
  }

  async function removeComment(item) {
    if (!window.confirm("Remove this comment?")) return;
    setCommentSaving(true);
    setCommentError("");
    try {
      await comments.remove(photo, item);
    } catch (error) {
      setCommentError(error.message);
    } finally {
      setCommentSaving(false);
    }
  }

  return (
    <div ref={stage} tabIndex={-1} className={full ? "photo-stage is-full" : "photo-stage"}>
      <div className="photo-stage__frame">
        <img src={photo.src} alt={photo.alt} style={cropStyle(photo.crop)} />
        <div className="photo-stage__wash" />
        <button className="icon-button photo-stage__expand" type="button" onClick={() => setFull((value) => !value)} aria-label={full ? "Close full screen photograph" : "View photograph full screen"}>
          <Expand aria-hidden="true" />
        </button>
        {curation && !full && (
          <div className="photo-stage__actions">
            {curation.makeCover && (
              <button
                className={`photo-stage__cover${isCover ? " is-cover" : ""}`}
                type="button"
                onClick={() => curation.makeCover(photo)}
                disabled={curation.busy || isCover}
                aria-pressed={isCover}
              >
                {makingCover ? <LoaderCircle className="is-spinning" aria-hidden="true" /> : <Star aria-hidden="true" />}
                {isCover ? "Cover photo" : makingCover ? "Setting cover…" : "Make cover photo"}
              </button>
            )}
            {curation.reorder && (
              <button
                className="photo-stage__order"
                type="button"
                onClick={() => {
                  setTargetPosition(visibleIndex + 1);
                  setOrderError("");
                  setOrderOpen((value) => !value);
                }}
                disabled={curation.busy || photos.length < 2}
                aria-expanded={orderOpen}
                aria-controls={`photo-order-${album.id}`}
              >
                {reordering ? <LoaderCircle className="is-spinning" aria-hidden="true" /> : <ListOrdered aria-hidden="true" />}
                {reordering ? "Saving order…" : "Change order"}
              </button>
            )}
            {curation.move && !photo.collegeDaysDisabled && (
              <button className="photo-stage__curate" type="button" onClick={() => curation.move(photo)} disabled={curation.busy}>
                {moving ? <LoaderCircle className="is-spinning" aria-hidden="true" /> : <BookImage aria-hidden="true" />}
                {moving ? "Moving to College Days…" : "Add to College Days"}
              </button>
            )}
            {curation.edit && (
              <button className="photo-stage__edit" type="button" onClick={() => curation.edit(photo)} disabled={curation.busy || editing}>
                <PencilLine aria-hidden="true" /> Edit photo
              </button>
            )}
            {curation.delete && (
              <button className="photo-stage__delete" type="button" onClick={() => curation.delete(photo)} disabled={curation.busy}>
                {deleting ? <LoaderCircle className="is-spinning" aria-hidden="true" /> : <Trash2 aria-hidden="true" />}
                {deleting ? "Deleting…" : "Delete photo"}
              </button>
            )}
          </div>
        )}
        <button className="photo-stage__arrow is-left" type="button" onClick={() => move(-1)} aria-label="Previous photograph">
          <ArrowLeft aria-hidden="true" />
        </button>
        <button className="photo-stage__arrow is-right" type="button" onClick={() => move(1)} aria-label="Next photograph">
          <ArrowRight aria-hidden="true" />
        </button>
        <div className="photo-stage__caption">
          <span>{String(visibleIndex + 1).padStart(2, "0")} / {String(photos.length).padStart(2, "0")}</span>
          <p>{photo.alt}</p>
        </div>
      </div>
      {sharePath && <PhotoShare key={`${album.id}:${photo.id}`} path={photoSharePath(sharePath, album.id, photo.id)} title={photo.alt || album.title} />}
      {curation?.reorder && orderOpen && (
        <form className="photo-stage__order-panel" id={`photo-order-${album.id}`} onSubmit={savePhotoOrder}>
          <div>
            <ListOrdered aria-hidden="true" />
            <span><b>Move this photograph</b><small>Currently {visibleIndex + 1} of {photos.length}</small></span>
          </div>
          <label>
            New position
            <input
              type="number"
              min="1"
              max={photos.length}
              required
              value={targetPosition}
              onChange={(event) => setTargetPosition(event.target.value)}
              aria-describedby={orderError ? `photo-order-error-${album.id}` : undefined}
            />
          </label>
          <span className="photo-stage__order-total">of {photos.length}</span>
          <button
            className="button button--small button--coral"
            type="submit"
            disabled={curation.busy || !Number.isInteger(Number(targetPosition)) || Number(targetPosition) < 1 || Number(targetPosition) > photos.length || Number(targetPosition) === visibleIndex + 1}
          >
            Save order
          </button>
          <button className="text-link" type="button" onClick={() => setOrderOpen(false)}>Cancel</button>
          {orderError && <p id={`photo-order-error-${album.id}`} role="alert">{orderError}</p>}
        </form>
      )}
      <input
        className="photo-stage__scrubber"
        type="range"
        min="0"
        max={photos.length - 1}
        value={visibleIndex}
        onChange={(event) => setIndex(Number(event.target.value))}
        aria-label={`Photograph ${visibleIndex + 1} of ${photos.length}`}
      />
      <div className="photo-stage__progress" aria-hidden="true">
        <span style={{ width: `${((visibleIndex + 1) / photos.length) * 100}%` }} />
      </div>
      {comments && (
        <section className={commentsOpen ? "reunion-photo-comments is-open" : "reunion-photo-comments"}>
          <button className="reunion-photo-comments__toggle" type="button" aria-expanded={commentsOpen} onClick={() => setCommentsOpen((value) => !value)}>
            <span><MessageCircle aria-hidden="true" /> {photo.comments?.length || 0} {(photo.comments?.length || 0) === 1 ? "comment" : "comments"}</span>
            <span>{commentsOpen ? "Hide" : "Open"}</span>
          </button>
          {commentsOpen && (
            <div className="reunion-photo-comments__panel">
              {photo.commentsDisabled && !editingComment ? (
                <p className="reunion-photo-comments__closed">Comments are closed for this photograph. A reunion editor can reopen them from the photograph’s edit button.</p>
              ) : comments.canContribute || editingComment ? (
                <form className="reunion-photo-comment-form" onSubmit={saveComment}>
                  <label htmlFor={`reunion-photo-comment-${photo.id}`}>{editingComment ? "Edit comment" : "Add a comment or memory"}</label>
                  <textarea id={`reunion-photo-comment-${photo.id}`} required maxLength="1200" rows="3" value={commentDraft} onChange={(event) => setCommentDraft(event.target.value)} />
                  <div>
                    <small>{commentDraft.length} / 1,200</small>
                    {editingComment && <button className="text-link" type="button" onClick={() => { setEditingComment(null); setCommentDraft(""); }}><X aria-hidden="true" /> Cancel edit</button>}
                    <button className="button button--small button--ink" type="submit" disabled={commentSaving || !commentDraft.trim()}>{commentSaving ? "Saving…" : editingComment ? "Save comment" : "Post comment"} <Send aria-hidden="true" /></button>
                  </div>
                </form>
              ) : null}
              {commentError && <p className="reunion-photo-comments__error" role="alert">{commentError}</p>}
              {(photo.comments?.length || 0) === 0 ? (
                <p className="reunion-photo-comments__empty">No comments yet.</p>
              ) : (
                <div className="reunion-photo-comment-list">
                  {photo.comments.map((item) => (
                    <article className="reunion-photo-comment" key={item.id}>
                      <p>{item.body}</p>
                      <footer>
                        <span><strong>{item.author.name}</strong> · {commentDate(item.createdAt)}{item.updatedAt !== item.createdAt ? " · edited" : ""}</span>
                        <div>
                          {item.canEdit && <button type="button" onClick={() => { setEditingComment(item.id); setCommentDraft(item.body); }}><PencilLine aria-hidden="true" /> Edit</button>}
                          {item.canDelete && <button type="button" onClick={() => removeComment(item)} disabled={commentSaving}><Trash2 aria-hidden="true" /> Remove</button>}
                        </div>
                      </footer>
                    </article>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
